import { useState, useRef, useEffect, useContext } from "react";

import { resObj, paginationVal } from "../../utils/constants";
import useAxios from "../../utils/useAxios";
import PermissionContext from "../../context/PermissionContext";
import DrawerContext from "../../context/DrawerContext";
import AutoLogout from "../../utils/useAutoLogout";

import Pagination from '@mui/material/Pagination';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import SideDrawer from "../../components/layout/SideDrawer";
import ErrorModal from "../../components/layout/ErrorModal";
import NoData from "../../components/layout/NoData";
import Loader from "../../components/layout/Loader";
import SuccessAlert from "../../components/layout/SuccessAlert";
import PlateStatusHeader from "../../components/analytics/PlateStatusHeader";
import PlateAnalysisTable from "../../components/analytics/PlateAnalysisTable";

const PlateAnalysisStatusPage = () => {
  const api = useAxios();
  const { permisionsList, checkUserPermission } = useContext(PermissionContext);
  const { drawerValue, setDrawerValue } = useContext(DrawerContext);

  useEffect(() => {
    if(permisionsList.length > 0) checkUserPermission(['Analytics-Dashboard']);
  }, [permisionsList]);

  const cpiAnalysisTableHeading = [
    'SI No', 'Plate', 'LNP Samples', 'LNP Parameter', 'DLS Summary', 'PSD Graph', 
    'Ribogreen', 'Transfection', 'siRNA Transfection',
  ];

  const uosAnalysisTableHeading = [
    'SI No', 'Sample Name', 'LNP Parameter Ignite', 'DLS', 'Ribogreen', 'Zeta Potential', 
  ];

  const mdcAnalysisTableHeading = [
    'SI No', 'Sample Name', 'Potency TE - HeLa', 'Potency TE - HEK293', 'Potency TE - HepG2', 'Potency TE - Primary heps', 
    'Endosomal Rupture', 'Biodistribution in vivo', 'Biodistribution ex vivo', 'LNP efficacy in Ex Vivo Tissue - Whole Tissue', 
    'LNP efficacy in Ex Vivo Tissue - Cell Type Specific', 'Mechanism of Internalization',
  ];

  const uolAnalysisTableHeading = [
    'SI No', 'Sample Name', 'DLS', 'LAL', 'Cytotoxicity', 
    'KU812', 'Complement', 'Coagulation', 
  ];

  const organisationArray = ['CPI', 'UoS', 'MDC', 'UoL'];

  const [apiRes, setApiRes] = useState(resObj);
  const pageData = useRef({...paginationVal});
  const [plateList, setPlateList] = useState([]);
  const [tableHeading, setTableHeading] = useState(cpiAnalysisTableHeading);
  const [tabValue, setTabValue] = useState('CPI');

  const navigateToEdit = () => {
    return;
  };

  useEffect(() => {
    getPageAnalysisStatus();
  }, []);

  useEffect(() => {
    setPlateList([]);
    pageData.current.pageNumber = 1;
    getPageAnalysisStatus();
    if (tabValue === 'CPI') {
      setTableHeading(cpiAnalysisTableHeading);
    } else if (tabValue === 'UoS') {
      setTableHeading(uosAnalysisTableHeading);
    } else if (tabValue === 'MDC') {
      setTableHeading(mdcAnalysisTableHeading);
    } else {
      setTableHeading(uolAnalysisTableHeading);
    }
  }, [tabValue]);

  const handlePaginationClick = (e, pageNumber) => {
    pageData.current.pageNumber = pageNumber;
    getPageAnalysisStatus();
  };

  const getPageAnalysisStatus = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/analytics/plate/analysis/status/get', {
        params: {
          page_number: +pageData.current.pageNumber,
          search_term: pageData.current.searchTerm,
          organisation: tabValue,
        }
      });

      if (response.status === 200) {
        setPlateList(response.data.data);
        pageData.current.pageCount = response.data.page_count;
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Query Result Get',
        loading: false,
      });
    }
  };

  const handleDownload = async () => {
    try {
      setApiRes({...apiRes, loading: true});
    
      const response = await api.get('/api/analytics/download/plate-status', {
        params: {
          search_term: pageData.current.searchTerm,
        },
        responseType: 'blob'
      });

      if (response.status === 200) {
        const fileDownload = require('js-file-download');
        fileDownload(response.data, 'Plate Status.xlsx');
        setApiRes({...apiRes, loading: false});
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Download File',
        loading: false,
      });
    }
  };

  return(
    <AutoLogout>
      {apiRes.loading && <Loader />}
      <SideDrawer drawerValue={drawerValue} setDrawerValue={setDrawerValue} />
      <div className="mainContainer" style={{ marginLeft: drawerValue.drawerWidth }}>
        {apiRes.showAlert && <SuccessAlert apiRes={apiRes} setApiRes={setApiRes} />}
        <PlateStatusHeader 
          title="Plate Status" 
          handleClick={navigateToEdit} 
          search={getPageAnalysisStatus}
          pageData={pageData}
          onDownload={handleDownload}
        />

        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ mb: 1 }}
        >
          {organisationArray.map((data, index) => (
            <Tab key={index} label={data} value={data} />
          ))}
        </Tabs>

        <PlateAnalysisTable
          heading={tableHeading} 
          rows={plateList} 
          pageData={pageData}
          style={{ marginTop: '4px' }}
        />

        {plateList.length === 0 && <NoData />}

        {(plateList.length !== 0 && pageData.current.pageCount !== 1) && <Pagination 
                            style={{marginTop: '10px', float: 'right'}} 
                            count={pageData.current.pageCount} 
                            onChange={handlePaginationClick}/>}

        <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />
      </div>
    </AutoLogout>
  );
};

export default PlateAnalysisStatusPage;